import { defineStore } from 'pinia'
import { useRequest } from 'composables/useRequest.js'

const CACHE_TTL = 5 * 60 * 1000 // 5 minutes

export const useTwoFactorStore = defineStore('two_factor', {
  state: () => ({
    status: null,
    fetchedAt: null,
    loading: false,
    error: null,
    // The security page renders the 2FA card twice, once in its mobile block
    // and once in its desktop block. Both mount even though only one is
    // visible, so without this they would fire the same request in parallel.
    inFlight: null,
  }),

  getters: {
    isCacheValid: (state) =>
      state.fetchedAt !== null && Date.now() - state.fetchedAt < CACHE_TTL,
    enabled: (state) => state.status?.enabled === true,
  },

  actions: {
    async fetchIfStale() {
      if (this.isCacheValid && this.status) return
      await this.fetchStatus()
    },

    async fetchStatus() {
      if (this.inFlight) return this.inFlight

      this.loading = true
      this.error = null

      this.inFlight = (async () => {
        try {
          const { axiosRequest } = useRequest()
          const res = await axiosRequest('GET', '/api/v1/two_factor', {}, false)
          if (!res.js_error && res.response.success) {
            this.status = res.response.data
            this.fetchedAt = Date.now()
          } else {
            this.error = 'Failed to load.'
          }
        } catch {
          this.error = 'Something went wrong.'
        } finally {
          this.loading = false
          this.inFlight = null
        }
      })()

      return this.inFlight
    },

    // Called after a successful enable/disable so the other copy of the card
    // and any later visit read the new state without a refetch.
    setStatus(status) {
      this.status = status
      this.fetchedAt = Date.now()
    },

    invalidate() {
      this.fetchedAt = null
    },
  },
})
